/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ChangeEvent, useMemo, useState } from "react";
import { When } from "react-if";
import styled from "styled-components";
import {
  Box,
  Button,
  FlexContainer,
  Input,
  Sidebar,
  theme,
} from "@nordcloud/gnui";
import { EstateV2Filter } from "~/generated/graphql";
import { UniversalWrap } from "~/components/Utils";
import { useEstateRecords } from "~/hooks";
import { showError } from "~/services/toast";
import { filterByName, inflect, isCloudResource } from "~/tools";
import { CloudResource } from "../EstateRecordsList";
import { PaginationState } from "../Pagination";
import { StandalonePagination } from "../StandalonePagination";
import { CloudResourcesMultiSelectorItemList } from "./CloudResourcesMultiSelectorItemList";

type CloudResourceMultiSelectorProps = {
  searchFilters: EstateV2Filter;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (items: CloudResource[]) => void;
  showCreate: boolean;
  onCreate: () => Promise<void>;
  name?: string;
  pluralName?: string;
  options?: CloudResource[];
};

type QueryProps = {
  limit: number;
  page: number;
  search: string;
};

const defaultOptions: CloudResource[] = [];

export function CloudResourcesMultiSelector({
  searchFilters,
  isOpen,
  name,
  pluralName,
  onSubmit,
  options = defaultOptions,
  onClose,
  showCreate,
  onCreate,
}: CloudResourceMultiSelectorProps) {
  const [selected, setSelected] = useState<CloudResource[]>(options);

  const initialQueryParam = {
    limit: 20,
    page: 0,
    search: "",
  };

  const [queryParams, setQueryParams] = useState<QueryProps>(initialQueryParam);

  const [searchInput, setSearchInput] = useState(queryParams.search ?? "");

  const { data, loading, error } = useEstateRecords(
    {
      variables: {
        limit: queryParams.limit,
        page: queryParams.page,
        filter: searchFilters,
        query: queryParams.search,
      },
    },
    showError
  );

  const filteredItems = useMemo(() => {
    const resources = data?.resources ?? [];

    return filterByName<CloudResource>(
      resources
        .filter((resource) => isCloudResource(resource))
        .map((resource) => resource as CloudResource),
      queryParams.search ?? ""
    );
  }, [data?.resources, queryParams.search]);

  const onSelect = (item: CloudResource) => {
    const newSelectedItems = selected.filter(({ id }) => id !== item.id);

    if (newSelectedItems.length === selected.length) {
      newSelectedItems.push(item);
    }

    setSelected(newSelectedItems);
  };

  const onSearchClick = () =>
    setQueryParams({
      limit: queryParams.limit,
      page: 0,
      search: searchInput,
    });

  const handlePaginationParamChange = (paginationParams: PaginationState) =>
    setQueryParams({
      limit: paginationParams.limit,
      page: paginationParams.page,
      search: searchInput,
    });

  const count = data?.count ?? 0;

  return (
    <Sidebar
      isOpen={isOpen}
      title={`Select ${name}`}
      footer={
        <SidebarFooter>
          <When condition={selected.length > 0}>
            <MultiSelectSubmitContainer>
              <Button type="button" onClick={() => onSubmit(selected)}>
                {getMultiSelectLabel(
                  selected.length,
                  name ?? "",
                  pluralName ?? ""
                )}
              </Button>
            </MultiSelectSubmitContainer>
          </When>
          <When condition={count > 0}>
            <StandalonePagination
              small
              count={count ?? 0}
              paginationParams={{
                page: queryParams.page,
                limit: queryParams.limit,
              }}
              onPaginationChange={handlePaginationParamChange}
            />
          </When>
        </SidebarFooter>
      }
      onClick={() => {
        setSelected(options);
        setQueryParams(initialQueryParam);
        onClose();
      }}
    >
      <When condition={showCreate}>
        <Button
          severity="low"
          size="md"
          icon="plus"
          ml={theme.spacing.spacing02}
          type="button"
          marginBottom={theme.spacing.spacing02}
          marginLeft={theme.spacing.spacing00}
          onClick={onCreate}
        >
          Create and Link Synthetic Record
        </Button>
      </When>
      <Box
        boxStyle="lightGrey"
        mb={theme.spacing.spacing04}
        innerSpacing="spacing02"
      >
        <FlexContainer>
          <Input
            small
            type="text"
            placeholder="Search"
            value={searchInput}
            onKeyPress={(event) => event.key === "Enter" && onSearchClick()}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSearchInput(event.target.value)
            }
          />
          <Button
            type="button"
            ml={theme.spacing.spacing02}
            icon="search"
            size="md"
            severity="low"
            onClick={onSearchClick}
          >
            Search
          </Button>
        </FlexContainer>
      </Box>
      <UniversalWrap
        loaderProps={{
          loading,
          inContent: true,
        }}
        errorProps={{
          error,
        }}
      >
        <CloudResourcesMultiSelectorItemList
          items={filteredItems}
          selectedOptions={selected}
          onSubmit={onSelect}
        />
      </UniversalWrap>
    </Sidebar>
  );
}

const SidebarFooter = styled.div`
  flex: 1;
`;

const MultiSelectSubmitContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.spacing03};
  background-color: ${theme.color.background.ui01};
  border-top: 1px solid ${theme.color.border.border01};
  position: sticky;
  bottom: 0;
  margin-right: auto;
  margin-left: auto;
`;

const getMultiSelectLabel = (
  selectedCount: number,
  name: string,
  pluralName?: string
) => {
  const inflectName = inflect(name, pluralName);
  return `${selectedCount} ${inflectName(selectedCount)} Selected`;
};
